import { formatToDateString } from '@naturehouse/nh-essentials/lib/dates/date';
import GTM from '../../../gtm/GTM';
import GTMViewItemModel from '../../../gtm/models/GTMViewItemModel';
import GTMDetailAddToCartButtonClickModel from '../../../gtm/models/GTMDetailAddToCartButtonClickModel';
import GTMDetailSelectOptionsModel from '../../../gtm/models/GTMDetailSelectOptionsModel';
import type { GA4CancellationPolicyType } from '../../../gtm/GTM.types';

export interface HouseDetailDataLayerData {
    custom_lastcountry: string;
    custom_lastregion: string;
    custom_lastdepartment: string;
    custom_lastcity: string;
    custom_bedrooms: number;
    custom_type: string;
    custom_theme: string;
    custom_service: string;
    custom_location: string | null;
    custom_durability: string;
    custom_reviews: number;
    custom_nature: number;
    currency: string;
    instant_booking: boolean;
    cancellation_policy: GA4CancellationPolicyType;
    products: dataLayerEcommerceProduct[];
}

interface PriceStructure {
    price: number;
    basePrice: number;
    discount: number;
    pricePerNight: number;
    discountPerNight: number;
}

export default class DetailFormTracking {
    readonly #formElement: HTMLFormElement;

    constructor(form: HTMLFormElement) {
        this.#formElement = form;
    }

    public static getHouseDetailDataLayerData(): HouseDetailDataLayerData | undefined {
        const dataLayerElement = document.getElementById('data-layer');
        const dataLayerContent = dataLayerElement?.getAttribute('content');

        if (!dataLayerElement || !dataLayerContent) {
            return undefined;
        }

        try {
            return JSON.parse(dataLayerContent) as HouseDetailDataLayerData;
        } catch {
            return undefined;
        }
    }

    public trackDetailPageImpression(quantity: number = 1): void {
        let houseData: HouseDetailDataLayerData | undefined;
        const dataLayerElement = document.getElementById('data-layer');

        if (dataLayerElement && dataLayerElement.hasAttribute('content')) {
            const dataLayerContent = dataLayerElement.getAttribute('content');
            if (!dataLayerContent) {
                return;
            }

            houseData = JSON.parse(dataLayerContent);
        }

        const house = houseData?.products?.[0];

        if (!houseData || !house) {
            return;
        }

        const priceStructure = this.#getPriceStructure();

        GTM.track(
            GTMViewItemModel.parseToGTMData({
                id: house.id,
                title: house.name,
                country: houseData.custom_lastcountry,
                region: houseData.custom_lastregion,
                department: houseData.custom_lastdepartment,
                place: houseData.custom_lastcity,
                discount: priceStructure.discountPerNight,
                pricePerNight: priceStructure.pricePerNight,
                instantBooking: houseData.instant_booking,
                quantity,
                cancellationPolicy: houseData.cancellation_policy
            })
        );
    }

    public trackDetailFormChange({
        dateRange,
        travelPartyData
    }: {
        dateRange: DateRange;
        travelPartyData: TravelPartyDataInterface;
    }): void {
        GTM.track(
            GTMDetailSelectOptionsModel.parseToGTMData({
                arrivalDate: dateRange.start ? formatToDateString(dateRange.start) : undefined,
                departureDate: dateRange.end ? formatToDateString(dateRange.end) : undefined,
                adults: travelPartyData.adults,
                children: travelPartyData.children,
                babies: travelPartyData.babies,
                pets: travelPartyData.pets
            })
        );
    }

    public processAddToCartData(
        dataLayer: Record<string, any>,
        quantity: number,
        dateSelection: DateRangeString
    ): void {
        if (!dateSelection.start || !dateSelection.end) {
            return;
        }

        const priceStructure = this.#getPriceStructure();

        GTM.trackEcommerce(
            GTMDetailAddToCartButtonClickModel.parseToGTMData({
                id: dataLayer.products[0].id,
                title: dataLayer.products[0].name,
                country: dataLayer.custom_lastcountry,
                region: dataLayer.custom_lastregion,
                department: dataLayer.custom_lastdepartment,
                place: dataLayer.custom_lastcity,
                pricePerNight: priceStructure.pricePerNight,
                discount: priceStructure.discountPerNight,
                instantBooking: dataLayer.instant_booking,
                quantity,
                cancellationPolicy: dataLayer.cancellation_policy
            })
        );
    }

    #getPriceStructure(): PriceStructure {
        const defaultPriceStructure: PriceStructure = {
            price: 0,
            basePrice: 0,
            discount: 0,
            pricePerNight: 0,
            discountPerNight: 0
        };

        const priceStructureElement: HTMLElement | null =
            this.#formElement.querySelector('[data-price-structure]');

        try {
            if (!priceStructureElement?.dataset?.priceStructure) {
                return defaultPriceStructure;
            }

            return JSON.parse(priceStructureElement.dataset.priceStructure) as PriceStructure;
        } catch {
            return defaultPriceStructure;
        }
    }
}
